import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Select, Input, DatePicker, message, Tooltip, Radio, Popover } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { View, Text } from 'styles/reactnative';
import { CloseOutlined, SearchOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Layout } from 'antd';
import { HeaderSection } from 'app/components/Layouts';
import ModernTabNavigation from 'app/components/ModernTabNavigation';
import API from 'utils/api';
import { useToken } from 'app/hooks/auth/token';
import { useWindowDimensions } from 'app/hooks';
import { API_URL } from 'app/constants/APIKEYS';
import { t } from 'i18next';
import Search from 'antd/es/input/Search';
import usePagination from 'app/hooks/usePagination';
import UserTag from 'app/components/UserTag';
import KrwPrice from 'app/components/PositionColumns/KrwPrice';
import AdminUserModal from 'app/components/AdminUserModal';
import * as XLSX from 'xlsx';
import { useUserGroup } from 'app/hooks/user/usergroups';
import SettingGroup from 'app/adminpages/AdminSystem/SettingGroup';

const { Content } = Layout;

interface DepositData {
  id: number;
  uid?: string;
  user_id: number;
  request_type: string;
  amount: string;
  order_id: string;
  status: string;
  pg_provider?: string;
  pg_tid?: string;
  data?: any;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  level?: number;
  group_name?: string;
  nickname?: any;
  fullname?: any;
  email?: any;
  phone?: any;
  loginid?: string;
  // 추가된 사용자 정보
  userInfo?: {
    nickname?: string;
    fullname?: string;
    email?: string;
    phone?: string;
    level?: number;
    group_name?: string;
    uid?: string;
    loginid?: string;
  };
}

const PGDepositPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [depositData, setDepositData] = useState<DepositData[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});

  // PG 입금에 필요한 상태 변수들
  const [user_id, setUser_id] = useState('');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [search, setSearch] = useState<any>('');
  const [tempid, setTempid] = useState('');
  const [group, setGroup] = useState<any>('*');
  const [keywordSearch, setKeywordSearch] = useState('');
  const [searchType, setSearchType] = useState('search_real_name'); // 기본값: 실명 검색

  const { data: token } = useToken();
  const { width } = useWindowDimensions();
  const { type1 } = useUserGroup();

  const { page, pageSize, totalCount, setPage, setPageSize, setTotalCount } =
    usePagination();

  // makeurl 함수 (일반 입금내역과 동일)
  const makeurl = (check: any, query: string) => {
    if (check) {
      return `&${query}=${check}`;
    } else {
      return '';
    }
  };

  // 사용자 정보를 가져오는 함수 (개별 API 호출)
  const fetchUserInfo = async (userIds: number[]) => {
    if (!token || userIds.length === 0) return {};

    try {
      const userInfoMap: { [key: number]: any } = {};
      
      // 각 사용자 ID에 대해 개별적으로 API 호출
      await Promise.all(
        userIds.map(async (userId) => {
          try {
            const userResponse = await API.get(
              `/admin/user/${userId}`,
              token + '',
              {}
            );

            if (userResponse.result?.success) {
              const userData = userResponse.result.result;
              
              userInfoMap[userId] = {
                uid: userData.uid || String(userData.id),
                loginid: userData.loginid,
                nickname: userData.nickname,
                fullname: userData.fullname,
                email: userData.email,
                phone: userData.phone,
                level: userData.level,
                group_name: userData.referral_code || null, // referral_code를 그룹으로 사용
              };
            }
          } catch (error) {
            console.error(`사용자 ${userId} 정보 조회 오류:`, error);
          }
        })
      );

      return userInfoMap;
    } catch (error) {
      console.error('사용자 정보 조회 오류:', error);
    }
    return {};
  };

  const fetchDepositList = async () => {
    if (!token) return;

    setLoading(true);
    try {
      // Payment API를 사용한 관리자 결제 요청 조회
      let url = `/payment/admin?request_type=DEPOSIT&limit=${pageSize}&offset=${(page - 1) * pageSize}`;

      if (user_id) {
        url += `&user_id=${user_id}`;
      }

      if (status) {
        url += `&status=${status}`;
      }

      // 기간 필터 추가
      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      // 키워드 검색 파라미터 추가
      if (keywordSearch && keywordSearch.trim()) {
        url += `&${searchType}=${encodeURIComponent(keywordSearch.trim())}`;
      }

      // 그룹 필터링 파라미터 추가
      if (group && group !== '*') {
        const filterGroups = Array.isArray(group) ? group : [group];
        if (!filterGroups.includes('*')) {
          // 여러 그룹이 선택된 경우 콤마로 구분하여 전송
          url += `&group_ids=${encodeURIComponent(filterGroups.join(','))}`;
        }
      }

      console.log('Payment API URL:', url);

      const response = await API.get(url, token, {}, false, API_URL);

      if (response.code === 0 || response.success) {
        const paymentData = response.result.data || [];
        
        // 고유한 user_id 목록 추출
        const userIds = [...new Set(paymentData.map((item: any) => item.user_id))];
        // 사용자 정보 가져오기
        const userInfoMap = await fetchUserInfo(userIds as number[]);
        
        // 결제 데이터에 사용자 정보 매칭
        let enrichedData = paymentData.map((item: any) => ({
          ...item,
          userInfo: userInfoMap[item.user_id] || null
        }));



        setDepositData(enrichedData);
        setTotalCount(response.result.total || 0);
      } else {
        message.error(response.message || '데이터를 가져오는데 실패했습니다');
      }
    } catch (error) {
      console.error('입금 요청 목록 조회 오류:', error);
      message.error('입금 요청 목록을 불러오는 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepositList();
  }, [page, pageSize]);

  useEffect(() => {
    setPage(1);
    fetchDepositList();
  }, [status, group, startDate, endDate, user_id, keywordSearch, searchType]);

  // 검색 함수
  const handleSearch = () => {
    setPage(1);
    fetchDepositList();
  };

  // 새로고침 함수
  const handleRefresh = () => {
    setPage(1);
    fetchDepositList();
  };

  // 엑셀 다운로드 함수 (페이징을 통한 전체 데이터 수집)
  const handleExcelDownload = async () => {
    if (!token) return;

    try {
      setLoading(true);
      
      let allPaymentData: any[] = [];
      let currentPage = 0;
      const pageSize = 100; // API 최대 허용 limit 값
      let hasMoreData = true;

      // 페이징을 통해 전체 데이터 수집
      while (hasMoreData) {
        let url = `/payment/admin?request_type=DEPOSIT&limit=${pageSize}&offset=${currentPage * pageSize}`;

        if (user_id) {
          url += `&user_id=${user_id}`;
        }

        if (status) {
          url += `&status=${status}`;
        }

        if (startDate) {
          url += `&start_date=${startDate}`;
        }
        
        if (endDate) {
          url += `&end_date=${endDate}`;
        }

        // 키워드 검색 파라미터 추가
        if (keywordSearch && keywordSearch.trim()) {
          url += `&${searchType}=${encodeURIComponent(keywordSearch.trim())}`;
        }

        // 그룹 필터링 파라미터 추가
        if (group && group !== '*') {
          const filterGroups = Array.isArray(group) ? group : [group];
          if (!filterGroups.includes('*')) {
            // 여러 그룹이 선택된 경우 콤마로 구분하여 전송
            url += `&group_ids=${encodeURIComponent(filterGroups.join(','))}`;
          }
        }

        console.log(`Excel Download API URL (Page ${currentPage + 1}):`, url);

        const response = await API.get(url, token, {}, false, API_URL);
        
        if (response.code === 0 || response.success) {
          const pageData = response.result?.data || response.data || response.result || [];
          
          if (pageData && pageData.length > 0) {
            allPaymentData = [...allPaymentData, ...pageData];
            
            // 가져온 데이터가 pageSize보다 적으면 마지막 페이지
            if (pageData.length < pageSize) {
              hasMoreData = false;
            } else {
              currentPage++;
            }
          } else {
            hasMoreData = false;
          }
        } else {
          console.error('Excel Download Error Response:', response);
          message.error(response.message || response.error || '데이터를 가져오는데 실패했습니다.');
          return;
        }
      }

      console.log(`Total collected data: ${allPaymentData.length} records`);

      if (!allPaymentData || allPaymentData.length === 0) {
        message.error('다운로드할 데이터가 없습니다.');
        return;
      }

      // 사용자 정보 가져오기
      const userIds = [...new Set(allPaymentData.map((item: any) => item.user_id))];
      const userInfoMap = await fetchUserInfo(userIds as number[]);
      
      // 결제 데이터에 사용자 정보 매칭
      let enrichedData = allPaymentData.map((item: any) => ({
        ...item,
        userInfo: userInfoMap[item.user_id] || null
      }));

      const excelData = enrichedData.map((item: any, index: number) => ({
        '번호': index + 1,
        '아이디': item.id,
        '회원코드': item.userInfo?.uid || item.uid || '-',
        '로그인ID': item.userInfo?.loginid || item.loginid || '-',
        '닉네임': item.userInfo?.nickname || item.nickname || '-',
        '실명': item.userInfo?.fullname || item.fullname || '-',
        '회원등급': item.userInfo?.level || item.level || '-',
        '그룹': item.userInfo?.group_name || item.group_name || '-',
        '요청금액': Number(item.amount) || 0,
        '주문ID': item.order_id || '-',
        'PG제공자': item.pg_provider || '-',
        'PG거래ID': item.pg_tid || '-',
        '상태': (() => {
          switch (item.status?.toUpperCase()) {
            case 'PENDING': return '대기';
            case 'APPROVED': return '승인';
            case 'REJECTED': return '거절';
            case 'PROCESSING': return '처리중';
            case 'COMPLETED': return '완료';
            case 'FAILED': return '실패';
            case 'CANCELLED': return '취소';
            default: return item.status;
          }
        })(),
        '요청일시': item.created_at ? dayjs(item.created_at).add(+9, 'hour').format('YYYY-MM-DD HH:mm:ss') : '-',
        '완료일시': (() => {
          if (item.completed_at) {
            return dayjs(item.completed_at).format('YYYY-MM-DD HH:mm:ss');
          }
          
          let completedTime = null;
          if (item.status?.toUpperCase() === 'APPROVED') {
            completedTime = item.data?.approval_time;
          } else if (item.status?.toUpperCase() === 'REJECTED') {
            completedTime = item.data?.rejection_time;
          } else if (item.status?.toUpperCase() === 'CANCELLED') {
            completedTime = item.data?.cancellation_time;
          }
          
          return completedTime ? dayjs(completedTime).format('YYYY-MM-DD HH:mm:ss') : '-';
        })(),
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'PG입금내역');

      const fileName = `PG입금내역_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      message.success('엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error);
      message.error('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleHold = async (record: DepositData) => {
    if (!token) return;

    try {
      setLoading(true);
      
      // Payment API를 사용한 결제 요청 수정 (관리자)
      const response = await API.put(
        `/payment/admin/${record.id}`,
        token,
        {
          status: 'PENDING', // 보류 상태로 변경
          data: {
            admin_note: '관리자에 의해 보류됨',
            hold_time: new Date().toISOString()
          }
        },
        API_URL
      );

      if (response.code === 0 || response.success) {
        message.success('입금 요청이 보류되었습니다');
        fetchDepositList();
      } else {
        message.error(response.message || '보류 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('보류 처리 오류:', error);
      message.error('보류 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (record: DepositData) => {
    if (!token) return;

    try {
      setLoading(true);
      
      // Payment API를 사용한 결제 요청 승인 (관리자)
      const response = await API.put(
        `/payment/admin/${record.id}`,
        token,
        {
          status: 'APPROVED',
          data: {
            admin_note: '관리자에 의해 승인됨',
            approval_time: new Date().toISOString()
          }
        },
        API_URL
      );

      if (response.code === 0 || response.success) {
        message.success('입금 요청이 승인되었습니다');
        fetchDepositList();
      } else {
        message.error(response.message || '승인 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('승인 처리 오류:', error);
      message.error('승인 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (record: DepositData) => {
    if (!token) return;

    try {
      setLoading(true);
      
      // Payment API를 사용한 결제 요청 거절 (관리자)
      const response = await API.put(
        `/payment/admin/${record.id}`,
        token,
        {
          status: 'REJECTED',
          data: {
            admin_note: '관리자에 의해 거절됨',
            rejection_time: new Date().toISOString()
          }
        },
        API_URL
      );

      if (response.code === 0 || response.success) {
        message.success('입금 요청이 거절되었습니다');
        fetchDepositList();
      } else {
        message.error(response.message || '거절 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('거절 처리 오류:', error);
      message.error('거절 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<DepositData> = [
    {
      title: '아이디',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
    },
         {
       title: '회원',
       dataIndex: 'user_id',
       key: 'user_id',
       align: 'center',
       render: (text, data) => (
         <UserTag 
           uid={data?.userInfo?.uid || data?.uid} 
           userid={data?.user_id} 
           level={data?.userInfo?.level || data?.level}
           nickname={data?.userInfo?.fullname || data?.fullname}
         />
       ),
     },
     {
       title: '이름',
       align: 'center',
       dataIndex: 'nickname',
       render: (text, data) => (
         <View>
           <Text style={{ justifyContent: 'flex-start' }}>
             {data?.userInfo?.nickname || data?.nickname || '-'}
           </Text>
           <Text style={{ justifyContent: 'flex-start' }}>
             ({data?.userInfo?.fullname || data?.fullname || '-'})
           </Text>
         </View>
       ),
     },
     {
       title: '아이디',
       align: 'center',
       dataIndex: 'loginid',
       render: (text, data) => (
         <Text style={{ justifyContent: 'flex-start' }}>
           {data?.userInfo?.loginid || data?.loginid || '-'}
         </Text>
       ),
     },
     {
       title: '그룹',
       align: 'center',
       dataIndex: 'group_name',
       render: (text, data) => {
         const groupName = data?.userInfo?.group_name || data?.group_name;
         return groupName ? (
           <Tooltip title={`그룹: ${groupName}`}>
             <Button size="small" type="default">
               {groupName}
             </Button>
           </Tooltip>
         ) : (
           <Text>-</Text>
         );
       },
     },
    {
      title: '요청 금액',
      dataIndex: 'amount',
      key: 'amount',
      align: 'center',
      render: (value: string) => <KrwPrice price={Number(value) || 0} />,
    },
    {
      title: '주문 ID',
      dataIndex: 'order_id',
      key: 'order_id',
      align: 'center',
      render: (value: string) => value ? value.substring(0, 12) + '...' : '-',
    },
    {
      title: 'PG 제공자',
      dataIndex: 'pg_provider',
      key: 'pg_provider',
      align: 'center',
      render: (value: string) => value || '-',
    },
    {
      title: '요청 일시',
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'center',
      render: (value: string) =>
        value
          ? dayjs(value).add(+9, 'hour').format('YYYY-MM-DD HH:mm:ss')
          : '-',
    },
    {
      title: '완료 일시',
      dataIndex: 'completed_at',
      key: 'completed_at',
      align: 'center',
      render: (value: string, record: DepositData) => {
        // completed_at이 있으면 사용
        if (value) {
          return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
        }
        
        // 상태에 따라 다른 시간 필드 사용
        let completedTime = null;
        if (record.status?.toUpperCase() === 'APPROVED') {
          completedTime = record.data?.approval_time;
        } else if (record.status?.toUpperCase() === 'REJECTED') {
          completedTime = record.data?.rejection_time;
        } else if (record.status?.toUpperCase() === 'CANCELLED') {
          completedTime = record.data?.cancellation_time;
        }
        
        return completedTime
          ? dayjs(completedTime).format('YYYY-MM-DD HH:mm:ss')
          : '-';
      },
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (value: string) => {
        switch (value?.toUpperCase()) {
          case 'PENDING':
            return '대기';
          case 'APPROVED':
            return '승인';
          case 'REJECTED':
            return '거절';
          case 'PROCESSING':
            return '처리중';
          case 'COMPLETED':
            return '완료';
          case 'FAILED':
            return '실패';
          case 'CANCELLED':
            return '취소';
          default:
            return value;
        }
      },
    },
    {
      title: '동작',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Space size={5}>
          <Button
            type="primary"
            onClick={() => handleApprove(record)}
            disabled={!['PENDING'].includes(record.status?.toUpperCase())}
          >
            승인
          </Button>
          <Button
            onClick={() => handleHold(record)}
            disabled={record.status?.toUpperCase() !== 'PENDING'}
            style={
              record.status?.toUpperCase() !== 'PENDING' 
                ? {} 
                : { backgroundColor: '#faad14', borderColor: '#faad14', color: '#fff' }
            }
          >
            보류
          </Button>
          <Button
            danger
            onClick={() => handleReject(record)}
            disabled={!['PENDING'].includes(record.status?.toUpperCase())}
          >
            거절
          </Button>
        </Space>
      ),
    },
  ];

  const items = [
    // {
    //   title: '전체',
    //   link: '/cash/pg',
    // },
    {
      title: 'PG 입금 관리',
      link: '/cash/pg/deposit',
    },
    {
      title: 'PG 출금 관리',
      link: '/cash/pg/withdraw',
    },
  ];

  const onRow = (e: any, record: any) => {
    const index = e.target?.cellIndex
      ? e.target?.cellIndex
      : e.target?.offsetParent?.cellIndex;
    if (index < 9) { // 파트너그룹 컬럼 제거로 인덱스 감소
      setSelectedData(record?.user_id);
      if (selectedData) {
        setShowModal(!showModal);
      }
    }
  };

  return (
    <Space direction='vertical' size={0} style={{ width: "100%" }}>
        <ModernTabNavigation items={items} />
        <View style={{ width: '100%', paddingTop: '10px' }}>
          <View
            style={{
              width: '100%',
              padding: 10,
              flexDirection: 'row',
              background: '#fff',
              marginTop: 5,
              marginBottom: 5,
              borderRadius: 10,
              flex: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                flex: 1,
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <Radio.Group
                onChange={e => {
                  setStatus(e.target.value);
                }}
                buttonStyle="solid"
                size="small"
                value={status}
                style={{ marginRight: 10 }}
              >
                <Radio.Button value={''}>전체</Radio.Button>
                <Radio.Button value={'PENDING'}>대기</Radio.Button>
                <Radio.Button value={'APPROVED'}>승인</Radio.Button>
                <Radio.Button value={'REJECTED'}>거절</Radio.Button>
                <Radio.Button value={'FAILED'}>실패</Radio.Button>
              </Radio.Group>

              <SettingGroup
                title={'그룹설정'}
                group={group}
                setGroup={setGroup}
                style={{ maxWidth: 200, borderBottom: 0 }}
                defaultv="*"
              />

              <Space direction="horizontal">
                <Select
                  value={searchType}
                  onChange={setSearchType}
                  style={{ width: 120 }}
                  size="small"
                >
                  <Select.Option value="search_real_name">실명</Select.Option>
                  <Select.Option value="search_user_code">회원코드</Select.Option>
                  <Select.Option value="search_login_id">로그인ID</Select.Option>
                </Select>
                <Space.Compact direction="horizontal">
                  <Input
                    placeholder={
                      searchType === 'search_real_name' ? '실명으로 검색' :
                      searchType === 'search_user_code' ? '회원코드로 검색' :
                      '로그인ID로 검색'
                    }
                    value={keywordSearch}
                    onChange={e => {
                      setKeywordSearch(e.target.value);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        // Enter 키 입력 시 즉시 검색 실행
                        setPage(1);
                        fetchDepositList();
                      }
                    }}
                    style={{ width: 200 }}
                  />
                  <Button
                    onClick={() => {
                      setPage(1);
                      fetchDepositList();
                    }}
                    style={{ paddingTop: 0 }}
                    size="small"
                    type="primary"
                  >
                    <SearchOutlined />
                  </Button>
                  {keywordSearch && (
                    <Button
                      onClick={() => {
                        setKeywordSearch('');
                        setPage(1);
                        fetchDepositList();
                      }}
                      style={{ paddingTop: 0 }}
                      size="small"
                    >
                      <CloseOutlined />
                    </Button>
                  )}
                </Space.Compact>
              </Space>
            </View>

            <Space>
              기간
              <Space.Compact>
                <DatePicker
                  onChange={e =>
                    setStartDate(
                      dayjs(e, 'YYYY-MM-DD').format('YYYY-MM-DD')
                    )
                  }
                  defaultValue={
                    startDate
                      ? dayjs(startDate, 'YYYY-MM-DD')
                      : undefined
                  }
                />
                <DatePicker
                  onChange={e =>
                    setEndDate(
                      dayjs(e, 'YYYY-MM-DD').format('YYYY-MM-DD')
                    )
                  }
                  defaultValue={
                    endDate
                      ? dayjs(endDate, 'YYYY-MM-DD')
                      : undefined
                  }
                />
              </Space.Compact>
              <Button
                onClick={handleExcelDownload}
                type="primary"
              >
                엑셀다운로드
              </Button>
              <Button
                onClick={handleRefresh}
                icon={<ReloadOutlined />}
                title="새로고침"
              />
            </Space>
          </View>

          <Table
            columns={columns}
            dataSource={depositData}
            loading={loading}
            rowKey="id"
            style={{ width: '100%' }}
            onRow={(record) => ({
              onClick: (e) => onRow(e, record),
            })}
            pagination={{
              current: page,
              total: totalCount,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}건`,
              onChange: (page, pageSize) => {
                setPage(page);
                setPageSize(pageSize);
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setPage(1);
              },
            }}
          />
        </View>

      <AdminUserModal
        showModal={showModal}
        setShowModal={setShowModal}
        selectedData={selectedData}
      />
    </Space>
  );
};

export default PGDepositPage;
